import forge from 'node-forge';
import { Auth0Client } from '@auth0/auth0-spa-js';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import UserStore from '../stores/UserStore';
import { AccountType, LanguageType } from '../swagger/api';
import { appInsights } from './appInsights';
import { activateMitIdViaPostMessage } from './wrapperAppFunctions';

const { pki } = forge;

const publicKeyPem = (process.env.REACT_APP_AUTH_PUBLIC_KEY || '').replace(/\\n/g, '\n');

const encryptPayload = (auth0UserId: string): string => {
  const timestamp = Date.now();
  const payloadObj = { id: auth0UserId, t: timestamp };
  const payload = JSON.stringify(payloadObj);

  if (!publicKeyPem) {
    throw new Error(
      'Public key is not defined. Please set REACT_APP_AUTH_PUBLIC_KEY in your environment variables.'
    );
  }

  if (!publicKeyPem.includes('BEGIN PUBLIC KEY') || !publicKeyPem.includes('END PUBLIC KEY')) {
    throw new Error(
      'Public key does not appear to be in valid PEM format. It must include BEGIN PUBLIC KEY and END PUBLIC KEY markers. The current value is incomplete or incorrectly read from REACT_APP_AUTH_PUBLIC_KEY. Check console logs for the full key value and verify your .env.development.local file.'
    );
  }

  const publicKey = pki.publicKeyFromPem(publicKeyPem);
  const encryptedBytes = publicKey.encrypt(payload, 'RSA-OAEP', {
    md: forge.md.sha256.create()
  });

  return forge.util
    .encode64(encryptedBytes)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
};

const activateMitIDLogin = async (
  auth0Client: Auth0Client,
  userStore: UserStore,
  userId?: string
) => {
  try {
    const auth0User = await auth0Client.getUser();
    const auth0UserId = userId || auth0User?.sub;

    if (!auth0UserId) {
      const error = new Error('Cannot activate MitID: Could not get Auth0 user ID');
      appInsights.trackException({ exception: error, severityLevel: SeverityLevel.Error });
      return false;
    }

    const isBusinessUser = userStore.currentAccount?.type === AccountType.Business;

    let encryptedState = '';
    try {
      encryptedState = encryptPayload(auth0UserId);
    } catch (encryptError) {
      const wrappedError = new Error(
        `Could not encrypt linking information. Please ensure the public key is valid and correctly formatted. Error: ${
          encryptError instanceof Error ? encryptError.message : String(encryptError)
        }`
      );
      appInsights.trackException({ exception: wrappedError, severityLevel: SeverityLevel.Error });

      if ((window as any).ReactNativeWebView) {
        return false;
      }
      throw wrappedError;
    }

    const userLanguage = userStore.person?.language || LanguageType.Danish;
    const uiLocales = userLanguage === LanguageType.Danish ? 'da' : 'en';

    // React Native app — send via postMessage instead of redirect
    if ((window as any).ReactNativeWebView) {
      const success = activateMitIdViaPostMessage(isBusinessUser, encryptedState, uiLocales);

      if (!success) {
        const error = new Error('Could not send MitID activation message to native app');
        appInsights.trackException({ exception: error, severityLevel: SeverityLevel.Error });
        return false;
      }

      return true;
    }

    // Web + Legacy app — redirect to MitID login via Auth0
    await auth0Client.loginWithRedirect({
      authorizationParams: {
        connection: 'Criipto',
        mklink: encryptedState,
        acr_values: isBusinessUser
          ? 'urn:grn:authn:dk:mitid:erhverv'
          : 'urn:grn:authn:dk:mitid:substantial',
        login_hint: 'action:approve',
        ui_locales: uiLocales
      }
    });

    return true;
  } catch (error) {
    const activationError = error instanceof Error ? error : new Error(String(error));
    appInsights.trackException({ exception: activationError, severityLevel: SeverityLevel.Error });
    return false;
  }
};

export default activateMitIDLogin;
